/**
 * Shared API client for the Talos gateway SPA (Preact port).
 *
 * All requests are session-authenticated via the OIDC session cookie (set by
 * the gateway). On a 401 when OIDC is enabled, the user is redirected to the
 * login flow because an expired/invalid session is the only cause of a 401.
 */

import { isOIDCEnabled } from './auth.js';

// fsContentURL returns a fetchable URL for a file's raw content by fs path
// (for <img/>/links). Each path segment is encoded so slashes stay separators.
export function fsContentURL(fsPath) {
  const segments = String(fsPath || '')
    .split('/')
    .map(encodeURIComponent)
    .join('/');
  return `/api/fs/${segments}`;
}

// apiFetch wraps fetch. Requests are authenticated by the OIDC session cookie;
// on a 401 the session is invalid/expired, so send the user to the login flow.
export async function apiFetch(url, options = {}) {
  const resp = await fetch(url, options);
  if (resp.status === 401 && isOIDCEnabled()) {
    window.location.assign('/auth/login');
  }
  return resp;
}

// uploadFile uploads a file to the workspace as an attachment (stored under
// the workspace's attachments directory) and returns its info.
export async function uploadFile(file) {
  const fd = new FormData();
  fd.append('file', file, file.name || 'file');
  const resp = await apiFetch('/api/upload', { method: 'POST', body: fd });
  if (!resp.ok) throw new Error(`upload failed (${resp.status})`);
  return resp.json();
}

// getFSTree returns the full workspace filesystem hierarchy with metadata.
export async function getFSTree() {
  const resp = await apiFetch('/api/fs/tree');
  if (!resp.ok) throw new Error(`list files failed (${resp.status})`);
  return resp.json();
}

// getFSText fetches a file's content as text (used for previews).
export async function getFSText(fsPath) {
  const resp = await apiFetch(fsContentURL(fsPath));
  if (!resp.ok) throw new Error(`fetch file failed (${resp.status})`);
  return resp.text();
}

// ---------------------------------------------------------------------------
// Cron
// ---------------------------------------------------------------------------

// listCron returns the cron jobs for the current workspace.
export async function listCron() {
  const resp = await apiFetch('/api/cron', { method: 'GET' });
  if (!resp.ok) throw new Error(`list cron failed (${resp.status})`);
  return resp.json();
}

// createCron creates a cron job from the given payload.
export async function createCron(job) {
  const resp = await apiFetch('/api/cron', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(job),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `create cron failed (${resp.status})`);
  }
  return resp.json();
}

// updateCron updates the cron job with the given id.
export async function updateCron(id, job) {
  const resp = await apiFetch(`/api/cron/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(job),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `update cron failed (${resp.status})`);
  }
  return resp.json();
}

// deleteCron deletes the cron job with the given id.
export async function deleteCron(id) {
  const resp = await apiFetch(`/api/cron/${id}`, { method: 'DELETE' });
  if (!resp.ok) throw new Error(`delete cron failed (${resp.status})`);
}

// setCronEnabled enables or disables the cron job with the given id.
export async function setCronEnabled(id, enabled) {
  const path = enabled ? 'enable' : 'disable';
  const resp = await apiFetch(`/api/cron/${id}/${path}`, { method: 'POST' });
  if (!resp.ok) throw new Error(`set cron enabled failed (${resp.status})`);
}

// runCron executes the cron job with the given id immediately.
export async function runCron(id) {
  const resp = await apiFetch(`/api/cron/${id}/run`, { method: 'POST' });
  if (!resp.ok) throw new Error(`run cron failed (${resp.status})`);
}

// ---------------------------------------------------------------------------
// Connectors
// ---------------------------------------------------------------------------

// listConnectorTypes returns the registered connector kinds with their config
// schemas and defaults (powers the "new connector" picker and editor).
export async function listConnectorTypes() {
  const resp = await apiFetch('/api/connectors/types', { method: 'GET' });
  if (!resp.ok) throw new Error(`list connector types failed (${resp.status})`);
  return resp.json();
}

// listConnectors returns the connector instances for the current workspace.
export async function listConnectors() {
  const resp = await apiFetch('/api/connectors', { method: 'GET' });
  if (!resp.ok) throw new Error(`list connectors failed (${resp.status})`);
  return resp.json();
}

// createConnector creates a connector instance from the given payload
// ({ kind, name, config }).
export async function createConnector(payload) {
  const resp = await apiFetch('/api/connectors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `create connector failed (${resp.status})`);
  }
  return resp.json();
}

// updateConnector updates a connector instance ({ name, enabled, config }).
export async function updateConnector(id, payload) {
  const resp = await apiFetch(`/api/connectors/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `update connector failed (${resp.status})`);
  }
  return resp.json();
}

// deleteConnector deletes the connector instance with the given id.
export async function deleteConnector(id) {
  const resp = await apiFetch(`/api/connectors/${id}`, { method: 'DELETE' });
  if (!resp.ok) throw new Error(`delete connector failed (${resp.status})`);
}

// ---------------------------------------------------------------------------
// Session messages
// ---------------------------------------------------------------------------

// loadEarlierMessages fetches older messages before the given message ID
// for client-side pagination when scrolling back through long sessions.
export async function loadEarlierMessages(sessionID, beforeID, limit = 100) {
  const resp = await apiFetch(
    `/api/s/${encodeURIComponent(sessionID)}/messages?before=${encodeURIComponent(beforeID)}&limit=${limit}`
  );
  if (!resp.ok) throw new Error(`load messages failed (${resp.status})`);
  return resp.json();
}

// markRead marks the session as read up to the given message ID. Only useful
// for web channels where the backend tracks an unread watermark.
export async function markRead(sessionID, lastID) {
  await apiFetch(`/api/s/${encodeURIComponent(sessionID)}/read`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ last_id: lastID }),
  });
}

// ---------------------------------------------------------------------------
// Push notifications
// ---------------------------------------------------------------------------

// getPushVapidKey returns the application server (VAPID) public key used to
// subscribe for web push.
export async function getPushVapidKey() {
  const resp = await apiFetch('/api/push/vapidPublicKey');
  if (!resp.ok) throw new Error(`fetch vapid key failed (${resp.status})`);
  const data = await resp.json();
  return data.publicKey;
}

// listPushSubscriptions returns the server-side push subscriptions.
export async function listPushSubscriptions() {
  const resp = await apiFetch('/api/push/subscription');
  if (!resp.ok) throw new Error(`list push subscriptions failed (${resp.status})`);
  return resp.json();
}

// savePushSubscription persists a browser PushSubscription (its toJSON()).
export async function savePushSubscription(sub) {
  const resp = await apiFetch('/api/push/subscription', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sub),
  });
  if (!resp.ok) throw new Error(`save push subscription failed (${resp.status})`);
}

// deletePushSubscription removes a push subscription by endpoint.
export async function deletePushSubscription(endpoint) {
  const resp = await apiFetch('/api/push/subscription', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  });
  if (!resp.ok) throw new Error(`delete push subscription failed (${resp.status})`);
}
